/**
 * TRITAL Care® ShiftBridge™ — central content registry for the
 * PRN marketplace shell at /shiftbridge/*.
 */

export const MARKETPLACE_BRAND = {
  name: "TRITAL ShiftBridge™",
  byline: "by TRITAL Care®",
  tagline: "Open shifts. Real rates. Vetted clinicians.",
};

/* Shift category cards (homepage + Browse) */
export const SHIFT_CATEGORIES = [
  { slug: "rn",        label: "Registered Nurse",   blurb: "ICU · ED · Med-Surg · L&D · OR · PACU",          icon: "HeartPulse" },
  { slug: "lpn-cna",   label: "LPN / CNA",          blurb: "Long-term care, acute, home health",             icon: "UserCheck" },
  { slug: "allied",    label: "Allied Health",      blurb: "Lab · Radiology · Respiratory · Sterile Proc.",  icon: "Microscope" },
  { slug: "therapy",   label: "Therapy",            blurb: "PT · OT · SLP — acute, SNF and outpatient",      icon: "Dumbbell" },
  { slug: "advanced",  label: "Advanced Practice",  blurb: "NP · PA · CRNA — locum and per diem",            icon: "Stethoscope" },
  { slug: "support",   label: "Clinical Support",   blurb: "MA · Phlebotomy · Surg Tech · EKG",              icon: "ClipboardList" },
];

/* How-it-works steps — clinician + facility variants */
export const HOW_IT_WORKS = {
  clinician: [
    { num: "01", title: "Sign up & verify",      blurb: "Profile + license + 2 credentials. Most clinicians are matched within 24 hours of upload." },
    { num: "02", title: "Browse open shifts",    blurb: "Filter by specialty, state, shift type and pay. Shifts are live — no fake postings." },
    { num: "03", title: "Claim with one tap",    blurb: "Direct facility confirmation. Pay rate locked at claim. Cancel-policy clearly stated." },
    { num: "04", title: "Show up & get paid",    blurb: "W-2 weekly direct deposit. PRN OT pays time-and-a-half on every shift over 8 hours." },
  ],
  facility: [
    { num: "01", title: "Create a facility account", blurb: "DON or scheduler signs up. Verify NPI + facility license. We approve within 1 business day." },
    { num: "02", title: "Post a shift",              blurb: "Specialty, time window, rate, requirements. We fan it out to matched clinicians instantly." },
    { num: "03", title: "Approve the claim",         blurb: "Auto-confirm matches, or review credentials before approving. Cancellation rules are yours." },
    { num: "04", title: "Pay one invoice",           blurb: "Net-30 consolidated invoicing. Multi-state payroll, taxes, workers comp — all on us." },
  ],
};

/* Trust pillars — homepage + facility / clinician pages */
export const TRUST_PILLARS = [
  { title: "Joint Commission-aligned",  blurb: "Every clinician on TRITAL ShiftBridge™ runs through TRITAL Care's 6-gate compliance pipeline before their first shift.", icon: "ShieldCheck" },
  { title: "Primary-source verified",   blurb: "License, BLS/ACLS, OIG, SAM, and immunization records re-verified at expiry — never expired credentials on shift.", icon: "BadgeCheck" },
  { title: "MBE-certified workforce",   blurb: "TRITAL Care® is NMSDC, HMSDC, NCTRCA-MBE, SBE-certified and SAM.gov registered — procurement-ready.",            icon: "Award" },
  { title: "W-2 only — never 1099",     blurb: "Workers' comp, tax remittance and unemployment insurance handled by TRITAL Care. Zero gig-misclassification risk.", icon: "Briefcase" },
];

/* Featured stats (homepage) */
export const HOMEPAGE_STATS = [
  { num: "92%",   label: "PRN fill rate at 7 days" },
  { num: "<24h",  label: "Average clinician approval" },
  { num: "$0",    label: "Marketplace fees for clinicians" },
  { num: "50",    label: "States covered" },
];

/* US-state list for filters & signup */
export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

/* Shift types (for filters + posting) */
export const SHIFT_TYPES = ["Day", "Evening", "Night", "Weekend", "On-Call", "12-hour", "8-hour"];

/* Specialty list (used by signup + filter) */
export const SPECIALTIES = [
  "ICU","ER","Med-Surg","Telemetry","L&D","NICU","PICU","OR","PACU","Cath Lab",
  "Oncology","Hospice","Home Health","LTC","Wound Care","Behavioral Health",
  "Stepdown","Case Management","Float Pool",
  "PT","OT","SLP","Respiratory Therapy",
  "Medical Assistant","CNA","LPN","Phlebotomy","Sterile Processing","Surg Tech",
  "Radiology Tech","Lab Tech","EMT","Pharmacy Tech",
  "Nurse Practitioner","Physician Assistant","CRNA",
];

/* Facility types (signup) */
export const FACILITY_TYPES = [
  "Acute Care Hospital","Critical Access Hospital","SNF / LTC","Assisted Living",
  "Outpatient Clinic","Surgery Center","Dialysis Center","Home Health Agency",
  "Hospice","Behavioral Health","Rehab Hospital","Urgent Care","Other",
];

/* FAQs — three audience groups */
export const FAQ_GROUPS = [
  {
    audience: "clinician",
    label: "For Clinicians",
    items: [
      { q: "Is TRITAL ShiftBridge™ free for clinicians?",
        a: "Yes — there are zero marketplace fees for clinicians. Posted rates are what you earn. TRITAL Care is paid by the facility." },
      { q: "How fast can I start picking up shifts?",
        a: "Most clinicians are credentialed and shift-ready within 24–72 hours of submitting their license, BLS/ACLS, and proof of immunizations. Some specialties (ICU, ED, OR) require an extra unit-specific competency check." },
      { q: "Am I a W-2 employee or 1099 contractor?",
        a: "W-2, always. TRITAL Care handles tax remittance, workers' comp, and unemployment insurance. You'll never face a gig-economy misclassification risk." },
      { q: "How is overtime calculated?",
        a: "Time-and-a-half on every hour over 8 in a single shift, or over 40 in a workweek — whichever applies in your state. Rate is locked at claim and never lowered." },
      { q: "What if I need to cancel a shift?",
        a: "Each posting shows the facility's cancellation window — typically 24–48 hours notice. Repeated late cancels can affect your matching priority but never your account standing without warning." },
    ],
  },
  {
    audience: "facility",
    label: "For Facilities",
    items: [
      { q: "How does pricing work?",
        a: "Bill-rate transparency — facilities pay one all-in hourly rate per shift. No surprise margins. Net-30 consolidated invoicing, with breakdowns by clinician + shift on every statement." },
      { q: "How is fill rate guaranteed?",
        a: "TRITAL ShiftBridge™ averages 92% fill rate at 7 days for facilities posting at market-rate. We surface low-rate alerts before you publish so you don't post a shift no one will claim." },
      { q: "Can we set unit-specific credentialing rules?",
        a: "Yes — every facility gets a rule editor for required competencies, immunization records, drug-screen window, and orientation hours per unit. TRITAL ShiftBridge™ blocks claims that fail your rules." },
      { q: "Who handles the clinician's tax + workers' comp?",
        a: "TRITAL Care employs every clinician W-2. Your facility receives a single invoice. We carry the workers' comp, unemployment, and federal/state tax remittance." },
      { q: "Is this Joint Commission audit-ready?",
        a: "Yes. Every clinician passes our 6-gate compliance pipeline (PSV license, OIG, SAM, drug screen, immunizations, BLS/ACLS) and an audit pack is available on demand at /compliance." },
    ],
  },
  {
    audience: "general",
    label: "Platform & Support",
    items: [
      { q: "Where is TRITAL ShiftBridge™ available?",
        a: "Clinicians can search available states and specialties. Shift availability depends on the currently published requests and facility eligibility." },
      { q: "Is there a mobile app?",
        a: "The web marketplace supports browsing and clinician registration. App availability and account features will be announced after release validation." },
      { q: "How do I get support?",
        a: "Email shifts@tritalcare.com or text our recruiter desk at (832) 344-1525 — both are real humans on the TRITAL Care team, not a bot tree." },
    ],
  },
];

/* CTA destinations — shared across all marketplace pages */
export const SHIFTS_CTAS = {
  facilitySignup:  "/shiftbridge/signup/facility",
  clinicianSignup: "/shiftbridge/signup/clinician",
  login:           "/shiftbridge/login",
  browse:          "/shiftbridge/browse",
  postShift:       "/shiftbridge/signup/facility?intent=post-shift",
  faq:             "/shiftbridge/faq",
  contact:         "/shiftbridge/contact",
};
